import {useData} from "@hps/hops-react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import RaildaysFeedbackBanner from "Raildays/Components/RaildaysFeedbackBanner.js";
import {slugify} from "Raildays/RaildaysUtils.js";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import Container from "Components/Container.js";
import IconButton from "Components/IconButton.js";
import Loader from "Components/Loader.js";
import RaildaysService from "Services/RaildaysService.js";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

const BRAND_COLOR = "rgb(207, 237, 237)";
const GOLD_COLOR = "rgb(226, 181, 52)";
const FALLBACK_EVENT_COLOR = "#9acce7";

const HERO_SLIDES = [
	{
		src: "/assets/Raildays/hero/photo2.jpeg",
		alt: "A family waves at the crew of a steam locomotive"
	},
	{
		src: "/assets/Raildays/hero/photo3.jpeg",
		alt: "The crew of a steam locomotive wave at a family"
	},
	{
		src: "/assets/Raildays/hero/photo4.jpeg",
		alt: "A young girl looks out the window of a heritage train coach"
	},
	{
		src: "/assets/Raildays/hero/photo5.jpeg",
		alt: "A happy family sit in a heritage train coach"
	},
	{
		src: "/assets/Raildays/hero/photo6.jpeg",
		alt: "A young family sit on a bench on a platform and wave at a train"
	},
	{
		src: "/assets/Raildays/hero/photo7.jpeg",
		alt: "A young family smile at a ticket collector on a train"
	},
	{
		src: "/assets/Raildays/hero/photo1.jpeg",
		alt: "A young boy walks down a train corridor making a thumbs-up sign"
	}
];

const SCREENSHOTS = [
	{
		src: "/assets/Raildays/screenshots/screenshot1.jpeg",
		alt: "Raildays App Screenshot #1"
	},
	{
		src: "/assets/Raildays/screenshots/screenshot2.jpeg",
		alt: "Raildays App Screenshot #2"
	},
	{
		src: "/assets/Raildays/screenshots/screenshot3.jpeg",
		alt: "Raildays App Screenshot #3"
	},
	{
		src: "/assets/Raildays/screenshots/screenshot4.jpeg",
		alt: "Raildays App Screenshot #4"
	},
	{
		src: "/assets/Raildays/screenshots/screenshot5.jpeg",
		alt: "Raildays App Screenshot #5"
	}
];

const RaildaysHomeView = () => {
	const isSmall = useMediaQuery("(max-width: 900px)");
	const [searchParams] = useSearchParams();
	const [activeSlide, setActiveSlide] = useState(0);
	const [eventImageErrors, setEventImageErrors] = useState({});

	const {data: eventTypesData, error: eventTypesError, loading: eventTypesLoading} = useData(
		useCallback(() => RaildaysService.getEventTypes(), [])
	);

	const {data: eventsData, error: eventsError, loading: eventsLoading} = useData(
		useCallback(() => RaildaysService.getEvents(), [])
	);

	useEffect(() => {
		if (HERO_SLIDES.length < 2) return undefined;
		const timer = window.setInterval(() => {
			setActiveSlide(prev => ((prev + 1) % HERO_SLIDES.length));
		}, 6000);
		return () => window.clearInterval(timer);
	}, []);

	const eventTypeCounts = useMemo(() => {
		const counts = new Map();
		if (!Array.isArray(eventsData)) return counts;
		eventsData.forEach(event => {
			if (!Array.isArray(event?.EventTypes)) return;
			event.EventTypes.forEach(type => {
				if (!type?.Id) return;
				counts.set(type.Id, (counts.get(type.Id) || 0) + 1);
			});
		});
		return counts;
	}, [eventsData]);

	const eventTypes = useMemo(() => (
		Array.isArray(eventTypesData) ? eventTypesData : []
	), [eventTypesData]);

	const displayEventTypes = useMemo(() => {
		if (!eventTypes.length) return [];
		if (!eventTypeCounts.size) return eventTypes;
		return eventTypes.filter(type => eventTypeCounts.get(type?.Id));
	}, [eventTypes, eventTypeCounts]);

	const totalErrors = eventTypesError || eventsError;

	const handlePrevSlide = useCallback(() => {
		setActiveSlide(prev => ((prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length));
	}, []);

	const handleNextSlide = useCallback(() => {
		setActiveSlide(prev => ((prev + 1) % HERO_SLIDES.length));
	}, []);

	const handleEventImageError = useCallback(typeId => {
		setEventImageErrors(prev => ({...prev, [typeId]: true}));
	}, []);

	return (
		<Container
			display="block"
			width="100%"
			px={{xs: 2, md: 4, lg: 6}}>
			<RaildaysFeedbackBanner show={searchParams.has("feedback")} />

			{totalErrors && (
				<Alert severity="error" sx={{mb: 3}}>
                        We could not load Raildays content just now. Please try again shortly.
				</Alert>
			)}

			<Box
				sx={{
					backgroundColor: "#fff",
					borderRadius: 2,
					p: {xs: 0.5, md: 1},
					mb: {xs: 3, md: 6},
					position: "relative",
					overflow: "hidden"
				}}>
				<Box sx={{position: "relative", height: {xs: 260, sm: 360, md: 560}}}>
					{HERO_SLIDES.map((slide, index) => (
						<Fade
							in={index === activeSlide}
							timeout={1000}
							key={slide.src}
							unmountOnExit>
							<Box
								component="img"
								alt={slide.alt}
								src={slide.src}
								sx={{
									position: "absolute",
									inset: 0,
									width: "100%",
									height: "100%",
									objectFit: "cover"
								}} />
						</Fade>
					))}

					<Stack
						direction="row"
						spacing={1}
						sx={{
							position: "absolute",
							bottom: 12,
							left: 12,
							backgroundColor: "rgba(0,0,0,0.35)",
							borderRadius: 99,
							px: 1.5,
							py: 0.5
						}}>
						{HERO_SLIDES.map((_, index) => (
							<Box
								key={index}
								sx={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									backgroundColor: index === activeSlide ? "#fff" : "rgba(255,255,255,0.4)"
								}} />
						))}
					</Stack>

					<IconButton
						label="Previous slide"
						onClick={handlePrevSlide}
						size={isSmall ? "small" : "medium"}
						style={{
							position: "absolute",
							top: "50%",
							left: 8,
							transform: "translateY(-50%)",
							backgroundColor: "rgba(255,255,255,0.7)"
						}}
						icon={ChevronLeftIcon} />

					<IconButton
						label="Next slide"
						onClick={handleNextSlide}
						size={isSmall ? "small" : "medium"}
						style={{
							position: "absolute",
							top: "50%",
							right: 8,
							transform: "translateY(-50%)",
							backgroundColor: "rgba(255,255,255,0.7)"
						}}
						icon={ChevronRightIcon} />
				</Box>
			</Box>

			<Box
				sx={{
					backgroundColor: "#fff",
					borderRadius: 2,
					mb: {xs: 3, md: 6},
					p: {xs: 3, md: 4}
				}}
			>
				<Stack direction={{xs: "column", md: "row"}} spacing={3} alignItems={{xs: "flex-start", md: "center"}} justifyContent="space-between">
					<Box>
						<Typography variant="h5" sx={{fontWeight: 700, mb: 1}}>
                                Find your next heritage railway adventure
						</Typography>
						<Typography color="text.secondary">
                                Browse railways across the UK, filter by type, and explore venues in detail.
						</Typography>
					</Box>
					<Button
						label="Explore railways"
						uri="/railways"
						variant="contained"
						color="primary"
						style={{padding: "12px 32px"}}
					/>
				</Stack>
			</Box>

			<Box
				sx={{
					borderRadius: 2,
					overflow: "hidden",
					mb: {xs: 3, md: 6}
				}}>
				<ImageList cols={isSmall ? 2 : 5} gap={0} sx={{m: 0}}>
					{SCREENSHOTS.map(shot => (
						<ImageListItem key={shot.src}>
							<Box
								component="img"
								alt={shot.alt}
								src={shot.src}
								sx={{
									width: "100%",
									height: "100%",
									objectFit: "cover"
								}} />
						</ImageListItem>
					))}
				</ImageList>
			</Box>

			<Box sx={{borderRadius: 2, overflow: "hidden", mb: {xs: 3, md: 6}}}>
				<Box
					sx={{
						backgroundColor: GOLD_COLOR,
						px: 3,
						py: 2,
						textAlign: "center"
					}}>
					<Typography variant="h5" sx={{color: "#fff", fontWeight: 700}}>
                            Events for every interest!
					</Typography>
				</Box>
				<Box sx={{backgroundColor: BRAND_COLOR, p: {xs: 2, md: 3}}}>
					{(eventTypesLoading || eventsLoading) && (
						<Stack alignItems="center" sx={{py: 4}}>
							<Loader />
						</Stack>
					)}
					{(!eventTypesLoading && !eventsLoading && !displayEventTypes.length) && (
						<Typography align="center" sx={{py: 3}}>
                                No event types are available yet.
						</Typography>
					)}
					<Grid container spacing={2}>
						{displayEventTypes.map(type => {
							const eventCount = eventTypeCounts.get(type?.Id);
							const eventImageUrl = `/assets/Raildays/event_type_images/${type?.Id}`;
							const showImage = type?.Id && !eventImageErrors[type?.Id];
							const label = eventCount ? `${type?.Name} (${eventCount})` : type?.Name;
							const typeSlug = type?.Slug || slugify(type?.Name || "");
							const href = typeSlug ? `/events?type=${encodeURIComponent(typeSlug)}` : null;

							return (
								<Grid item key={type?.Id || type?.Name} xs={12} sm={6} md={4} lg={3} xl={2}>
									<Card
										px={0}
										py={0}
										uri={href || undefined}
										style={{
											height: "100%",
											backgroundColor: type?.Colour || FALLBACK_EVENT_COLOR,
											color: "#fff"
										}}>
										{showImage ?
											(
												<Box
													component="img"
													alt={type?.Name}
													src={eventImageUrl}
													onError={() => handleEventImageError(type?.Id)}
													sx={{
														width: "100%",
														height: 180,
														objectFit: "cover",
														display: "block"
													}} />
											) :
											(
												<Stack
													alignItems="center"
													justifyContent="center"
													sx={{height: 180}}>
													<EventAvailableIcon sx={{fontSize: 48}} />
												</Stack>
											)}
										<Container
											display="block"
											px={0}
											py={0}
											style={{
												height: 80,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												textAlign: "center",
												padding: 12
											}}>
											<Typography variant="subtitle1" sx={{fontWeight: 700}}>
												{label || "Untitled"}
											</Typography>
										</Container>
									</Card>
								</Grid>
							);
						})}
					</Grid>
				</Box>
			</Box>

			<Box
				sx={{
					backgroundColor: "#fff",
					borderRadius: 2,
					mb: {xs: 3, md: 6},
					p: {xs: 3, md: 4}
				}}
			>
				<Stack direction={{xs: "column", md: "row"}} spacing={3} alignItems={{xs: "flex-start", md: "center"}} justifyContent="space-between">
					<Box>
						<Typography variant="h5" sx={{fontWeight: 700, mb: 1}}>
                            Find your next heritage railway adventure
						</Typography>
						<Typography color="text.secondary">
                            Browse railways across the UK, filter by type, and explore venues in detail.
						</Typography>
					</Box>
					<Button
						label="Explore railways"
						uri="/railways"
						variant="contained"
						color="primary"
						style={{padding: "12px 32px"}}
					/>
				</Stack>
			</Box>

			<Box
				sx={{
					borderRadius: 2,
					overflow: "hidden",
					position: "relative",
					backgroundColor: BRAND_COLOR
				}}>
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						display: "flex",
						pointerEvents: "none",
						opacity: 0.9
					}}>
					<Box sx={{width: "50%", display: "flex", alignItems: "flex-end"}}>
						<Box
							component="img"
							alt=""
							src="/assets/Raildays/footer/raildays-footer-1.png"
							sx={{
								width: "100%",
								objectFit: "contain",
								display: {xs: "none", md: "block"}
							}} />
					</Box>
					<Box sx={{width: "50%", display: "flex", alignItems: "flex-end"}}>
						<Box
							component="img"
							alt=""
							src="/assets/Raildays/footer/raildays-footer-2.png"
							sx={{
								width: "100%",
								objectFit: "contain",
								display: {xs: "none", md: "block"}
							}} />
					</Box>
				</Box>

				<Box sx={{position: "relative", py: {xs: 5, md: 10}}}>
					<Container display="block" maxWidth="960px" mx="auto">
						<Typography variant={isSmall ? "h5" : "h4"} align="center" sx={{mb: {xs: 4, md: 6}}}>
							250+ UK railway venues for a great family Railday out!
						</Typography>
						<Stack
							direction={isSmall ? "column" : "row"}
							spacing={3}
							alignItems="center"
							justifyContent="center">
							<Box
								component="a"
								href="https://apps.apple.com/gb/app/id1569791008"
								title="Raildays on App Store"
								target="_blank"
								rel="noreferrer">
								<Box
									component="img"
									alt="Available on the App Store"
									src="/assets/Raildays/store/astore.png"
									sx={{maxWidth: 220}} />
							</Box>
							<Box
								component="a"
								href="https://play.google.com/store/apps/details?id=com.raildays.android"
								title="Raildays on Google Play"
								target="_blank"
								rel="noreferrer">
								<Box
									component="img"
									alt="Get it on Google Play"
									src="/assets/Raildays/store/gplay.png"
									sx={{maxWidth: 220}} />
							</Box>
						</Stack>

						<Box
							sx={{
								mt: {xs: 4, md: 6},
								mx: "auto",
								maxWidth: 640,
								p: {xs: 3, md: 4},
								borderRadius: 2,
								backgroundColor: "rgba(255,255,255,0.7)",
								textAlign: "center"
							}}>
							<Typography variant="h6" sx={{mb: 2}}>
                                Raildays is a mobile app which contains information and events from the UK's rich heritage railway scene.
							</Typography>
							<Typography variant="h6">
                                Whether you're local, a family, a tourist, or a railway enthusiast, Raildays is the app for you!
							</Typography>
						</Box>
					</Container>
				</Box>
			</Box>
		</Container>
	);
};

export default RaildaysHomeView;
